import React from "react";
import {
  Box,
  Autocomplete,
  TextField,
  useMediaQuery,
  useTheme,
  Paper,
  Typography,
  IconButton,
  Tooltip,
  Chip,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import ClearIcon from "@mui/icons-material/Clear";
import TuneIcon from "@mui/icons-material/Tune";

import { areaOptions } from "../../../constants/areas";
import { functionOptions } from "../../../constants/functions";
import { routeOptions } from "../../../constants/routes";

// Format string from snake_case to Title Case
const formatOptionLabel = (str) => {
  return str
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

function FilterToolbar({ filters, setFilters }) {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const handleChange = (field) => (_, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value || undefined,
    }));
  };

  const handleClearAll = () => {
    setFilters({});
  };

  const activeFiltersCount = Object.values(filters).filter(Boolean).length;

  const sharedAutocompleteStyles = {
    "& .MuiOutlinedInput-root": {
      fontSize: "0.875rem",
      backgroundColor: theme.palette.background.paper,
      transition: "all 0.2s ease-in-out",
      borderRadius: "8px",
      border: `1px solid ${theme.palette.divider}`,
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: theme.palette.text.secondary,
      },

      "&:hover": {
        backgroundColor: `${theme.palette.primary.main}05`,
      },
      "&.Mui-focused": {
        boxShadow: theme.palette.text.secondary,
      },
    },
    "& .MuiAutocomplete-input": {
      padding: "8px 12px !important",
    },
    "& .MuiAutocomplete-endAdornment": {
      right: "8px",
    },
    "& .MuiSvgIcon-root": {
      color: theme.palette.text.secondary,
    },
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        backgroundColor: theme.palette.background.paper,
        borderRadius: "12px",
        transition: "all 0.2s ease-in-out",
        border: "none",
        boxShadow: "none",
        "& .MuiPaper-root": {
          border: "none",
          boxShadow: "none",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              backgroundColor: theme.palette.primary.main,
              borderRadius: "8px",
              p: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <TuneIcon sx={{ color: "white", fontSize: 20 }} />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 0.5 }}>
              Filter Contacts
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {activeFiltersCount
                ? `${activeFiltersCount} active filter${
                    activeFiltersCount > 1 ? "s" : ""
                  }`
                : "No filters applied"}
            </Typography>
          </Box>
        </Box>
        {Object.keys(filters).length > 0 && (
          <Tooltip title="Clear all filters">
            <IconButton
              onClick={handleClearAll}
              sx={{
                backgroundColor: `${theme.palette.error.main}10`,
                color: theme.palette.error.main,
                "&:hover": {
                  backgroundColor: `${theme.palette.error.main}20`,
                },
              }}
            >
              <ClearIcon />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: isSmallScreen ? "column" : "row",
          gap: 2,
          width: "100%",
        }}
      >
        <Autocomplete
          value={filters.function || null}
          onChange={handleChange("function")}
          options={functionOptions}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Select function"
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ ml: 1, mr: 1, fontWeight: 500 }}
                  >
                    Function:
                  </Typography>
                ),
              }}
            />
          )}
          renderOption={(props, option) => (
            <li {...props}>
              <Typography variant="body2">
                {formatOptionLabel(option)}
              </Typography>
            </li>
          )}
          getOptionLabel={formatOptionLabel}
          sx={{
            flexGrow: 1,
            minWidth: isSmallScreen ? "100%" : 200,
            ...sharedAutocompleteStyles,
          }}
        />
        <Autocomplete
          value={filters.route || null}
          onChange={handleChange("route")}
          options={routeOptions}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Select route"
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ ml: 1, mr: 1, fontWeight: 500 }}
                  >
                    Route:
                  </Typography>
                ),
              }}
            />
          )}
          renderOption={(props, option) => (
            <li {...props}>
              <Typography variant="body2">
                {formatOptionLabel(option)}
              </Typography>
            </li>
          )}
          getOptionLabel={formatOptionLabel}
          sx={{
            flexGrow: 1,
            minWidth: isSmallScreen ? "100%" : 200,
            ...sharedAutocompleteStyles,
          }}
        />
        <Autocomplete
          value={filters.area || null}
          onChange={handleChange("area")}
          options={areaOptions}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Select area"
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ ml: 1, mr: 1, fontWeight: 500 }}
                  >
                    Area:
                  </Typography>
                ),
              }}
            />
          )}
          renderOption={(props, option) => (
            <li {...props}>
              <Typography variant="body2">
                {formatOptionLabel(option)}
              </Typography>
            </li>
          )}
          getOptionLabel={formatOptionLabel}
          sx={{
            flexGrow: 1,
            minWidth: isSmallScreen ? "100%" : 200,
            ...sharedAutocompleteStyles,
          }}
        />
      </Box>
    </Paper>
  );
}

export default React.memo(FilterToolbar);
